import { create } from 'zustand'

/**
 * Asks to present, in both directions.
 *
 * Screen sharing needs the presenter capability, which only a moderator can
 * grant. Before this, an attendee without it simply had no share button and no
 * way to ask — the capability existed and was unreachable.
 *
 * Two halves, deliberately in one store because they are two ends of the same
 * exchange and reading them together is how you reason about it:
 *
 *   - `incoming` — what moderators must answer. A queue rather than a single
 *     slot: two people can ask at once, and dropping one because another
 *     arrived would leave someone waiting forever on an answer that is never
 *     coming.
 *   - `myOutcome` — the answer to MY request, or null while waiting. Approval
 *     has no event of its own: `moderate:set_presenter` rebroadcasts the roles
 *     and the client notices it may now present, which is why this is set from
 *     two different places.
 */
import type { PresenterRequestSubject } from '@vc/views/dialogs'
import type { Actor } from '../services/moderator-notices'

export type PresenterRequest = {
  /** SFU peerId of the asker — what `moderate:set_presenter` targets. */
  peerId: string
  name: string
  /** What they want presenter FOR — the moderator's dialog says so, because
   *  "wants to share their screen" for a document request is not answerable. */
  subject: PresenterRequestSubject
  /**
   * A screen-share session asking (contract §8): its name is whatever was
   * typed on its page, so the card says "(Screen share)" and shows the
   * account's email beside it (decision 6).
   */
  shareOnly?: boolean
  /** The asker's account email, when the server sends one. */
  email?: string | null
}

type PresenterRequestsState = {
  incoming: PresenterRequest[]
  myOutcome: 'approved' | 'rejected' | null
  /** The moderator who refused it, so the notice can name them. */
  myOutcomeBy: Actor | null
  /**
   * Subjects learned from the out-of-band `broadcast` relay, keyed by peerId.
   *
   * The subject reaches moderators by two routes and either can arrive first,
   * so it is remembered rather than dropped: a hint that lands before the
   * request is applied when the request shows up, and one that lands after
   * updates the request in place.
   */
  subjectHints: Record<string, PresenterRequestSubject>
  /** Queue an ask. Ignores a duplicate — asking twice is one question. */
  add: (request: PresenterRequest) => void
  /** Record what an ask was FOR, whichever order the two messages arrive in. */
  noteSubject: (peerId: string, subject: PresenterRequestSubject) => void
  /** Answered (either way), or the asker left. */
  remove: (peerId: string) => void
  setMyOutcome: (outcome: 'approved' | 'rejected' | null, by?: Actor | null) => void
  clear: () => void
}

export const usePresenterRequestsStore = create<PresenterRequestsState>((set) => ({
  incoming: [],
  myOutcome: null,
  myOutcomeBy: null,
  subjectHints: {},
  add: (request) =>
    set((prev) =>
      prev.incoming.some((r) => r.peerId === request.peerId)
        ? prev
        : {
            incoming: [
              ...prev.incoming,
              // A hint that beat the request wins: it is the specific answer,
              // where `request.subject` may only be the screen-share fallback.
              { ...request, subject: prev.subjectHints[request.peerId] ?? request.subject },
            ],
          },
    ),
  noteSubject: (peerId, subject) =>
    set((prev) => ({
      subjectHints: { ...prev.subjectHints, [peerId]: subject },
      incoming: prev.incoming.map((r) => (r.peerId === peerId ? { ...r, subject } : r)),
    })),
  remove: (peerId) =>
    set((prev) => {
      const { [peerId]: _dropped, ...subjectHints } = prev.subjectHints
      return { incoming: prev.incoming.filter((r) => r.peerId !== peerId), subjectHints }
    }),
  setMyOutcome: (myOutcome, by = null) => set({ myOutcome, myOutcomeBy: by }),
  clear: () => set({ incoming: [], myOutcome: null, myOutcomeBy: null, subjectHints: {} }),
}))

/** The one being answered — oldest first, so nobody is starved by later asks. */
export const selectNextRequest = (s: PresenterRequestsState): PresenterRequest | null =>
  s.incoming[0] ?? null
